/**
 * EditableField - Componente React para campos editables in-line
 * 
 * Un componente versátil que permite editar diferentes tipos de datos directamente
 * en la interfaz, con soporte para validación, múltiples tipos de input y estados.
 * 
 * @param {Object} props - Propiedades del componente
 * 
 * === PROPIEDADES BÁSICAS ===
 * @param {string} field - Identificador único del campo (requerido)
 * @param {string} label - Etiqueta visible del campo (requerido)
 * @param {any} value - Valor actual del campo
 * @param {string} [type='text'] - Tipo de campo. Valores posibles:
 *   - 'text': Texto simple
 *   - 'number': Números
 *   - 'date': Solo fecha (YYYY-MM-DD)
 *   - 'datetime': Fecha y hora (YYYY-MM-DDTHH:mm)
 *   - 'time': Solo hora (HH:mm)
 *   - 'email': Email con validación
 *   - 'tel': Teléfono
 *   - 'textarea': Área de texto multi-línea
 *   - 'select': Select nativo de HTML
 *   - 'react-select': Select avanzado con búsqueda
 * 
 * === OPCIONES Y CONFIGURACIÓN ===
 * @param {Array} [options=[]] - Opciones para select/react-select. Formatos:
 *   - Strings: ['opcion1', 'opcion2']
 *   - Objetos: [{value: 'val1', label: 'Etiqueta 1'}, {value: 'val2', label: 'Etiqueta 2'}]
 * @param {string} [placeholder=''] - Texto placeholder
 * @param {boolean} [isAdmin=true] - Si mostrar botones de edición
 * @param {boolean} [disabled=false] - Si el campo está deshabilitado
 * @param {boolean} [required=false] - Si el campo es obligatorio
 * @param {string} [className=''] - Clases CSS adicionales
 * 
 * === VALIDACIÓN Y RESTRICCIONES ===
 * @param {Function} [validation=null] - Función de validación personalizada
 *   Formato: (value) => string|null (retorna mensaje de error o null)
 * @param {number|string} [min=null] - Valor mínimo (números/fechas)
 * @param {number|string} [max=null] - Valor máximo (números/fechas)
 * @param {number} [rows=3] - Número de filas para textarea
 * 
 * === CALLBACKS ===
 * @param {Function} onSave - Función llamada al guardar
 *   Formato: async (field, newValue) => void
 * @param {Function} [onCancel] - Función llamada al cancelar
 *   Formato: (field) => void
 * @param {Function} [onInputChange] - Función llamada en cada cambio del input
 *   Formato: (field, currentValue, rawValue) => void
 *   - field: identificador del campo
 *   - currentValue: valor procesado según el tipo de campo
 *   - rawValue: valor crudo del input/evento
 * 
 * === FORMATEO Y VISUALIZACIÓN ===
 * @param {Function} [formatDisplay=null] - Función para formatear la visualización
 *   Formato: (value) => string
 * 
 * === PROPIEDADES ESPECÍFICAS PARA REACT-SELECT ===
 * @param {boolean} [isClearable=true] - Si se puede limpiar la selección
 * @param {boolean} [isSearchable=true] - Si se puede buscar en las opciones
 * @param {boolean} [isLoading=false] - Si mostrar indicador de carga
 * @param {Function} [noOptionsMessage] - Mensaje cuando no hay opciones
 * @param {Function} [getOptionFromValue=null] - Función para convertir valor a opción
 *   Formato: (options, value) => option|null
 * 
 * === EJEMPLOS DE USO ===
 * 
 * // Campo de texto con callback de cambio
 * <EditableField
 *   field="nombre"
 *   label="Nombre"
 *   value="Juan Pérez"
 *   type="text"
 *   required={true}
 *   onSave={(field, value) => console.log(`Guardado ${field}: ${value}`)}
 *   onInputChange={(field, value, raw) => console.log(`Escribiendo en ${field}: ${value}`)}
 * />
 * 
 * // Campo numérico con validación en tiempo real
 * <EditableField
 *   field="precio"
 *   label="Precio"
 *   value={29.99}
 *   type="number"
 *   min={0}
 *   onInputChange={(field, value) => {
 *     if (value < 0) {
 *       console.warn('Precio no puede ser negativo');
 *     }
 *   }}
 *   onSave={(field, value) => updatePrice(field, value)}
 * />
 * 
 * // React-select con búsqueda dinámica
 * <EditableField
 *   field="categoria"
 *   label="Categoría"
 *   value="tech"
 *   type="react-select"
 *   options={categories}
 *   onInputChange={(field, selectedOption, inputValue) => {
 *     // selectedOption es el objeto completo seleccionado
 *     // inputValue es el texto que el usuario está escribiendo para buscar
 *     console.log('Selección cambiada:', selectedOption);
 *   }}
 *   onSave={(field, value) => updateCategory(field, value)}
 * />
 */

import React, { useState, useEffect } from 'react';
import { Edit3, Save, X, Calendar, ChevronDown } from 'lucide-react';
import Select from 'react-select';

const EditableField = ({
  field,
  label,
  value,
  type = 'text',
  options = [],
  placeholder = '',
  isAdmin = false,
  onSave,
  onCancel,
  onInputChange,
  validation = null,
  disabled = false,
  required = false,
  min = null,
  max = null,
  rows = 3,
  className = '',
  formatDisplay = null,
  isClearable = true,
  isSearchable = true,
  isLoading = false,
  noOptionsMessage = () => "No se encontraron resultados",
  getOptionFromValue = null,
  // Nueva prop para debug
  debug = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');

  // Debug logging
  useEffect(() => {
    if (debug) {
      console.log(`[EditableField ${field}] Props changed:`, {
        value,
        isEditing,
        editValue,
        type,
        options: options?.length || 0
      });
    }
  }, [value, isEditing, editValue, field, debug, type, options]);

  // Efecto para sincronizar el valor cuando cambia desde el padre
  useEffect(() => {
    if (!isEditing && value !== undefined) {
      const formattedValue = formatValueForInput(value, type);
      if (debug) {
        console.log(`[EditableField ${field}] Syncing value:`, { value, formattedValue });
      }
    }
  }, [value, isEditing, field, type, debug]);

  // Función helper para normalizar opciones
  const normalizeOptions = (opts) => {
    return opts.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return option;
    });
  };

  // Función helper para encontrar la opción correspondiente al valor
  const findOptionByValue = (val) => {
    if (!val || !options.length) return null;
    
    const normalizedOpts = normalizeOptions(options);
    
    // Si se proporciona una función personalizada, usarla
    if (getOptionFromValue) {
      return getOptionFromValue(normalizedOpts, val);
    }
    
    // Buscar la opción que coincida con el valor
    return normalizedOpts.find(opt => opt.value === val) || null;
  };

  // Función para formatear valores según el tipo de input
  const formatValueForInput = (val, inputType) => {
    if (!val && val !== 0) return '';
    
    try {
      switch (inputType) {
        case 'date': { // Lo separamos de nuevo
          const date = new Date(val);
          if (isNaN(date.getTime())) return '';
          
          // Construimos la fecha usando la HORA LOCAL
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          
          // Devolvemos solo la fecha, en el formato que el input espera
          return `${year}-${month}-${day}`;
        }
        case 'datetime':
          if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
            return val.slice(0, 16); // Asegurar formato YYYY-MM-DDTHH:mm
          }
          const datetime = new Date(val);
          if (isNaN(datetime.getTime())) return '';
          const year = datetime.getFullYear();
          const month = String(datetime.getMonth() + 1).padStart(2, '0');
          const day = String(datetime.getDate()).padStart(2, '0');
          const hours = String(datetime.getHours()).padStart(2, '0');
          const minutes = String(datetime.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        
        case 'time':
          if (val.includes('T')) {
            return val.split('T')[1].slice(0, 5);
          }
          return val;
        
        case 'react-select':
          return findOptionByValue(val);
        
        default:
          return val;
      }
    } catch (error) {
      console.error('Error formatting value:', error);
      return '';
    }
  };

  const handleEdit = () => {
    if (disabled) return;
    
    const formattedValue = formatValueForInput(value, type);
    if (debug) {
      console.log(`[EditableField ${field}] Starting edit:`, { 
        originalValue: value, 
        formattedValue,
        type 
      });
    }
    
    setIsEditing(true);
    setEditValue(formattedValue);
    setError('');
  };

  const handleSave = async () => {
    let valueToSave = editValue;
    
    if (debug) {
      console.log(`[EditableField ${field}] Attempting save:`, { editValue, type });
    }
    
    // Para react-select, extraer el valor de la opción seleccionada
    if (type === 'react-select') {
      valueToSave = editValue ? editValue.value : null;
    }

     // ✅ ---- INICIO DE LA CORRECCIÓN CLAVE ---- ✅
    // Si es un campo de fecha y tiene un valor, lo convertimos a un objeto Date.
    if ((type === 'date' || type === 'datetime') && valueToSave) {
      
      // Para 'date' (que viene como 'YYYY-MM-DD'), le agregamos una hora
      // para que Javascript lo interprete como hora local y no UTC.
      const dateString = type === 'date' ? `${valueToSave}T00:00:00` : valueToSave;
      
      // Creamos el objeto Date a partir del string de hora local.
      valueToSave = new Date(dateString);
    }
    // ✅ ---- FIN DE LA CORRECCIÓN CLAVE ---- ✅
    
    // Validación
    if (required && (!valueToSave || valueToSave.toString().trim() === '')) {
      setError('Este campo es obligatorio');
      return;
    }

    if (validation) {
      const validationError = validation(valueToSave);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    try {
      if (onSave) {
        if (debug) {
          console.log(`[EditableField ${field}] Calling onSave with:`, valueToSave);
        }
        await onSave(field, valueToSave);
      }
      setIsEditing(false);
      setError('');
      if (debug) {
        console.log(`[EditableField ${field}] Save completed successfully`);
      }
    } catch (err) {
      if (debug) {
        console.error(`[EditableField ${field}] Save error:`, err);
      }
      setError(err.message || 'Error al guardar');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setError('');
    if (onCancel) {
      onCancel(field);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    
    if (debug) {
      console.log(`[EditableField ${field}] Input change:`, { newValue, type });
    }
    
    setEditValue(newValue);
    
    // Llamar al callback onInputChange si está definido
    if (onInputChange) {
      try {
        onInputChange(field, newValue, e);
      } catch (err) {
        if (debug) {
          console.error(`[EditableField ${field}] onInputChange error:`, err);
        }
      }
    }
  };

  const handleSelectChange = (option) => {
    if (debug) {
      console.log(`[EditableField ${field}] Select change:`, { option, type });
    }
    
    setEditValue(option);
    
    // Llamar al callback onInputChange si está definido
    if (onInputChange) {
      try {
        onInputChange(field, option, option ? option.value : null);
      } catch (err) {
        if (debug) {
          console.error(`[EditableField ${field}] onInputChange error:`, err);
        }
      }
    }
  };

  // Función especial para manejar cambios en el input de búsqueda de react-select
  const handleSelectInputChange = (inputValue, actionMeta) => {
    if (debug && actionMeta.action === 'input-change') {
      console.log(`[EditableField ${field}] Select input change:`, { inputValue, actionMeta });
    }
    
    // Solo llamar al callback si es un cambio del usuario escribiendo
    if (onInputChange && actionMeta.action === 'input-change') {
      try {
        onInputChange(field, inputValue, { action: 'search', inputValue });
      } catch (err) {
        if (debug) {
          console.error(`[EditableField ${field}] onInputChange error:`, err);
        }
      }
    }
  };

  const renderInput = () => {
    const commonProps = {
      value: editValue || '',
      onChange: handleChange,
      onKeyDown: handleKeyPress,
      className: `w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
        error ? 'border-red-500' : 'border-gray-300'
      }`,
      placeholder,
      autoFocus: true,
      disabled: disabled
    };

    switch (type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={rows}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancel();
            }}
          />
        );

      case 'number':
        return (
          <input
            {...commonProps}
            type="number"
            min={min}
            max={max}
            step="any"
          />
        );

      case 'date':
        return (
          <input
            {...commonProps}
            type="date"
            min={min}
            max={max}
          />
        );

      case 'datetime':
        return (
          <input
            {...commonProps}
            type="datetime-local"
            min={min}
            max={max}
          />
        );

      case 'time':
        return (
          <input
            {...commonProps}
            type="time"
            min={min}
            max={max}
          />
        );

      case 'react-select':
        const normalizedOptions = normalizeOptions(options);

        return (
          <Select
            value={editValue}
            onChange={handleSelectChange}
            onInputChange={handleSelectInputChange}
            options={normalizedOptions}
            placeholder={placeholder}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isLoading={isLoading}
            isDisabled={disabled}
            noOptionsMessage={noOptionsMessage}
            classNamePrefix="react-select"
            className={error ? 'react-select-error' : ''}
            autoFocus={true}
            openMenuOnFocus={false}
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: '42px',
                border: `1px solid ${error ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                boxShadow: state.isFocused ? `0 0 0 2px ${error ? '#fecaca' : '#dbeafe'}` : 'none',
                backgroundColor: 'white',
                fontSize: '14px',
                '&:hover': {
                  borderColor: error ? '#ef4444' : '#9ca3af'
                }
              }),
              valueContainer: (provided) => ({
                ...provided,
                padding: '8px 12px'
              }),
              placeholder: (provided) => ({
                ...provided,
                color: '#9ca3af',
                fontSize: '14px'
              }),
              singleValue: (provided) => ({
                ...provided,
                color: '#374151',
                fontSize: '14px'
              }),
              menu: (provided) => ({
                ...provided,
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                zIndex: 9999
              }),
              menuList: (provided) => ({
                ...provided,
                borderRadius: '6px',
                padding: '4px'
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected 
                  ? '#3b82f6' 
                  : state.isFocused 
                    ? '#f3f4f6' 
                    : 'transparent',
                color: state.isSelected ? 'white' : '#374151',
                padding: '12px 16px',
                fontSize: '14px',
                borderRadius: '4px',
                margin: '2px 0',
                cursor: 'pointer',
                '&:active': {
                  backgroundColor: state.isSelected ? '#2563eb' : '#e5e7eb'
                }
              }),
              indicatorSeparator: (provided) => ({
                ...provided,
                backgroundColor: '#d1d5db'
              }),
              dropdownIndicator: (provided, state) => ({
                ...provided,
                color: '#6b7280',
                padding: '8px',
                '&:hover': {
                  color: '#374151'
                }
              }),
              clearIndicator: (provided) => ({
                ...provided,
                color: '#6b7280',
                padding: '8px',
                '&:hover': {
                  color: '#374151'
                }
              }),
              loadingIndicator: (provided) => ({
                ...provided,
                color: '#3b82f6'
              }),
              noOptionsMessage: (provided) => ({
                ...provided,
                color: '#6b7280',
                fontSize: '14px',
                padding: '8px 12px'
              })
            }}
          />
        );

      case 'select':
        return (
          <div className="relative">
            <select
              {...commonProps}
              className={`${commonProps.className} appearance-none pr-8`}
            >
              <option value="">Seleccionar...</option>
              {options.map((option, index) => (
                <option 
                  key={index} 
                  value={typeof option === 'object' ? option.value : option}
                >
                  {typeof option === 'object' ? option.label : option}
                </option>
              ))}
            </select>
            <ChevronDown 
              size={16} 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" 
            />
          </div>
        );

      case 'email':
        return (
          <input
            {...commonProps}
            type="email"
          />
        );

      case 'tel':
        return (
          <input
            {...commonProps}
            type="tel"
          />
        );

      default:
        return (
          <input
            {...commonProps}
            type="text"
          />
        );
    }
  };

  const displayValue = () => {
    if (formatDisplay) {
      return formatDisplay(value);
    }

    if (!value && value !== 0) return 'No especificado';

    switch (type) {
      case 'date':
        if (value) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
        }
        return 'No especificado';

      case 'datetime':
      if (value) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,  // Agregar esta línea
            timeZone: 'America/Bogota'  // Y esta línea
          });
        }
      }
      return 'No especificado';

      case 'time':
        if (value) {
          return value;
        }
        return 'No especificado';

      case 'number':
        if (typeof value === 'number') {
          return value.toLocaleString('es-ES');
        }
        return value;

      case 'select':
        if (options.length > 0 && typeof options[0] === 'object') {
          const selectedOption = options.find(opt => opt.value === value);
          return selectedOption ? selectedOption.label : value;
        }
        return value;

      case 'react-select':
        if (value && options.length > 0) {
          const selectedOption = findOptionByValue(value);
          return selectedOption ? selectedOption.label : value;
        }
        return value;

      default:
        return value;
    }
  };

  return (
    <div className={`flex items-start justify-between p-3 bg-gray-50 rounded-lg ${className}`}>
      <div className="flex-1 min-w-0">
        <label className="text-sm text-gray-600 block mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {isEditing ? (
          <div>
            {renderInput()}
            {error && (
              <p className="text-red-500 text-xs mt-1">{error}</p>
            )}
          </div>
        ) : (
          <span className={`font-medium text-gray-800 break-words ${
            (!value && value !== 0) ? 'text-gray-400 italic' : ''
          }`}>
            {displayValue()}
          </span>
        )}
      </div>
      
      {isAdmin && (
        <div className="ml-4 flex gap-2 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={disabled}
                className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Guardar"
              >
                <Save size={16} />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 text-red-600 hover:bg-red-100 rounded"
                title="Cancelar"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              disabled={disabled}
              className="p-1 text-blue-600 hover:bg-blue-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Editar"
            >
              <Edit3 size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;